/**
 * QuickStats Component
 *
 * A compact bar showing key phage metrics at a glance, plus direct access to
 * primary records, synchronized saved state, exact sharing, and citations.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePhageStore } from '@phage-explorer/state';
import { useOverlay } from './overlays/OverlayProvider';
import { haptics } from '../utils/haptics';
import { buildShareUrl, getGeneShareKey, parseShareState } from '../utils/share-state';
import {
  getPhageCollectionKey,
  readPhageCollections,
  subscribeToPhageCollections,
  toggleFavoritePhage,
} from '../utils/phage-collections';
import {
  buildNcbiNucleotideUrl,
  buildPhageCitation,
  buildRcsbPdbUrl,
} from '../utils/research-record';
import { summarizeGeneStrands } from '../utils/gene-strand';
import '../styles/quick-stats-actions.css';

export { buildNcbiNucleotideUrl, buildRcsbPdbUrl } from '../utils/research-record';

interface QuickStatsProps {
  className?: string;
}

type ActionFeedback = 'accession-copied' | 'link-copied' | 'citation-copied' | null;

async function copyText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Fall through to the selection-based copy path.
    }
  }

  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.setAttribute('readonly', '');
  textArea.style.position = 'fixed';
  textArea.style.opacity = '0';
  textArea.style.pointerEvents = 'none';
  document.body.appendChild(textArea);
  textArea.select();
  const copied = document.execCommand('copy');
  textArea.remove();
  if (!copied) throw new Error('Clipboard copy failed');
}

function ExternalLinkIcon(): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

function ShareIcon(): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <path d="m8.6 10.5 6.8-4" />
      <path d="m8.6 13.5 6.8 4" />
    </svg>
  );
}

function CopyIcon(): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}

function StarIcon({ filled }: { filled: boolean }): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m12 2.8 2.8 5.7 6.3.9-4.6 4.4 1.1 6.3-5.6-3-5.6 3 1.1-6.3-4.6-4.4 6.3-.9L12 2.8Z" />
    </svg>
  );
}

function CitationIcon(): React.ReactElement {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" />
      <path d="M8 7h8" />
      <path d="M8 11h6" />
    </svg>
  );
}

export function QuickStats({ className = '' }: QuickStatsProps): React.ReactElement | null {
  const { open } = useOverlay();
  const currentPhage = usePhageStore((state) => state.currentPhage);
  const selectedGeneId = usePhageStore((state) => state.selectedGeneId);
  const viewMode = usePhageStore((state) => state.viewMode);
  const readingFrame = usePhageStore((state) => state.readingFrame);
  const scrollPosition = usePhageStore((state) => state.scrollPosition);
  const show3DModel = usePhageStore((state) => state.show3DModel);
  const [feedback, setFeedback] = useState<ActionFeedback>(null);
  const [isSaved, setIsSaved] = useState(false);
  const feedbackTimerRef = useRef<number | null>(null);

  const selectedGene = useMemo(() => {
    if (!currentPhage || selectedGeneId === null) return null;
    return currentPhage.genes.find((gene) => gene.id === selectedGeneId) ?? null;
  }, [currentPhage, selectedGeneId]);

  const selectedGeneLabel = useMemo(() => {
    if (!selectedGene) return null;
    return (
      selectedGene.locusTag?.trim() ||
      selectedGene.name?.trim() ||
      selectedGene.product?.trim() ||
      `Gene ${selectedGene.id}`
    );
  }, [selectedGene]);

  const stats = useMemo(() => {
    if (!currentPhage) return null;

    const strandSummary = summarizeGeneStrands(currentPhage.genes ?? []);
    const pdbIds = Array.from(
      new Set(
        (currentPhage.pdbIds ?? [])
          .map((pdbId) => pdbId.trim().toUpperCase())
          .filter(Boolean)
      )
    );

    return {
      id: currentPhage.id,
      name: currentPhage.name,
      slug: currentPhage.slug,
      accession: currentPhage.accession,
      localGenome: currentPhage.localGenome,
      length: currentPhage.genomeLength,
      gcContent: currentPhage.gcContent,
      geneCount: currentPhage.genes?.length ?? 0,
      forwardGenes: strandSummary.forward,
      reverseGenes: strandSummary.reverse,
      unknownStrandGenes: strandSummary.unknown,
      baltimore: currentPhage.baltimoreGroup,
      host: currentPhage.host,
      pdbIds,
      hasPdb: pdbIds.length > 0,
    };
  }, [currentPhage]);

  const showFeedback = useCallback((nextFeedback: Exclude<ActionFeedback, null>) => {
    setFeedback(nextFeedback);
    if (feedbackTimerRef.current !== null) {
      window.clearTimeout(feedbackTimerRef.current);
    }
    feedbackTimerRef.current = window.setTimeout(() => {
      setFeedback(null);
      feedbackTimerRef.current = null;
    }, 1800);
  }, []);

  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current !== null) {
        window.clearTimeout(feedbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!stats) {
      setIsSaved(false);
      return;
    }

    const key = getPhageCollectionKey(stats);
    const syncSavedState = (favoriteKeys: readonly string[]) => {
      setIsSaved(favoriteKeys.includes(key));
    };

    syncSavedState(readPhageCollections().favoriteKeys);
    return subscribeToPhageCollections((snapshot) => syncSavedState(snapshot.favoriteKeys));
  }, [stats]);

  const ncbiUrl = stats ? buildNcbiNucleotideUrl(stats.accession) : '';
  const primaryPdbId = stats?.pdbIds[0] ?? null;
  const pdbUrl = primaryPdbId ? buildRcsbPdbUrl(primaryPdbId) : '';
  const shareUrl = useMemo(() => {
    if (!stats || stats.localGenome) return '';
    const baseUrl = typeof window === 'undefined'
      ? 'https://phage-explorer.org/'
      : window.location.href;
    const currentTool = parseShareState(baseUrl).tool;

    return buildShareUrl(baseUrl, {
      phageKey: stats.slug?.trim() || stats.accession,
      geneKey: getGeneShareKey(selectedGene),
      viewMode,
      position: scrollPosition,
      readingFrame,
      show3DModel,
      tool: currentTool,
    });
  }, [readingFrame, scrollPosition, selectedGene, show3DModel, stats, viewMode]);

  const handleCopyAccession = useCallback(async () => {
    if (!stats) return;
    try {
      await copyText(stats.accession);
      haptics.success();
      showFeedback('accession-copied');
    } catch {
      haptics.error();
    }
  }, [showFeedback, stats]);

  const handleCopyCitation = useCallback(async () => {
    if (!stats || !shareUrl) return;
    const citation = buildPhageCitation({
      name: selectedGeneLabel ? `${selectedGeneLabel} in ${stats.name}` : stats.name,
      accession: stats.accession,
      pdbIds: stats.pdbIds,
      explorerUrl: shareUrl,
    });

    try {
      await copyText(citation);
      haptics.success();
      showFeedback('citation-copied');
    } catch {
      haptics.error();
    }
  }, [selectedGeneLabel, shareUrl, showFeedback, stats]);

  const handleToggleSaved = useCallback(() => {
    if (!stats) return;
    const snapshot = toggleFavoritePhage(stats);
    const key = getPhageCollectionKey(stats);
    const nextSaved = snapshot.favoriteKeys.includes(key);
    setIsSaved(nextSaved);
    haptics.success();
  }, [stats]);

  const handleShare = useCallback(async () => {
    if (!stats || !shareUrl) return;
    const shareData = {
      title: selectedGeneLabel
        ? `${selectedGeneLabel} — ${stats.name} — Phage Explorer`
        : `${stats.name} — Phage Explorer`,
      text: selectedGeneLabel
        ? `Explore ${selectedGeneLabel} in ${stats.name} (${stats.accession}) at this exact genome position and view.`
        : `Explore ${stats.name} (${stats.accession}) at this exact genome position and view in Phage Explorer.`,
      url: shareUrl,
    };

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share(shareData);
        haptics.success();
        return;
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        // Fall back to copying the exact URL when native sharing is unavailable at call time.
      }
    }

    try {
      await copyText(shareUrl);
      haptics.success();
      showFeedback('link-copied');
    } catch {
      haptics.error();
    }
  }, [selectedGeneLabel, shareUrl, showFeedback, stats]);

  if (!stats) {
    return null;
  }

  const formatNumber = (value: number): string => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
    return value.toLocaleString();
  };

  const strandAriaLabel = [
    `${stats.forwardGenes} forward strand`,
    `${stats.reverseGenes} reverse strand`,
    ...(stats.unknownStrandGenes > 0
      ? [`${stats.unknownStrandGenes} unknown strand`]
      : []),
  ].join(', ');

  return (
    <div className={`quick-stats ${className}`} aria-label="Phage statistics">
      <div className="quick-stat">
        <span className="quick-stat__label">Length</span>
        <span className="quick-stat__value">
          {stats.length === null || stats.length === undefined ? 'Not reported' : `${formatNumber(stats.length)} bp`}
        </span>
      </div>

      {stats.gcContent !== null && stats.gcContent !== undefined && (
        <div className="quick-stat">
          <span className="quick-stat__label">GC Content</span>
          <span className="quick-stat__value">{stats.gcContent.toFixed(1)}%</span>
        </div>
      )}

      <div className="quick-stat">
        <span className="quick-stat__label">Genes</span>
        <span className="quick-stat__value">
          {stats.geneCount}{' '}
          <span className="quick-stat__detail" aria-label={strandAriaLabel}>
            ({stats.forwardGenes}+ / {stats.reverseGenes}-
            {stats.unknownStrandGenes > 0 ? ` / ${stats.unknownStrandGenes}?` : ''})
          </span>
        </span>
      </div>

      {stats.baltimore && (
        <div className="quick-stat">
          <span className="quick-stat__label">Baltimore</span>
          <span className="quick-stat__value quick-stat__value--highlight">{stats.baltimore}</span>
        </div>
      )}

      {stats.host && (
        <div className="quick-stat quick-stat--wide">
          <span className="quick-stat__label">Host</span>
          <span className="quick-stat__value quick-stat__value--host">{stats.host}</span>
        </div>
      )}

      {stats.hasPdb && (
        <div className="quick-stat">
          <span className="quick-stat__label">Structure</span>
          <span className="quick-stat__value quick-stat__value--highlight">
            {stats.pdbIds.length === 1 ? 'Available' : `${stats.pdbIds.length} records`}
          </span>
        </div>
      )}

      <div className="quick-stat">
        <span className="quick-stat__label">Accession</span>
        <span className="quick-stat__value quick-stat__value--mono">{stats.accession}</span>
      </div>

      <div className="quick-stat quick-stat--actions">
        <span className="quick-stat__label">Research</span>
        <div className="quick-stat__actions">
          {!stats.localGenome && <a
            className="quick-stat__action"
            href={ncbiUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${stats.accession} in NCBI Nucleotide`}
          >
            <ExternalLinkIcon />
            <span>NCBI</span>
          </a>}
          {stats.localGenome && <span>Local {stats.localGenome.topology} DNA</span>}
          {primaryPdbId && (
            <a
              className="quick-stat__action"
              href={pdbUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={stats.pdbIds.length > 1 ? `Structures: ${stats.pdbIds.join(', ')}` : undefined}
              aria-label={`Open PDB structure ${primaryPdbId}${stats.pdbIds.length > 1 ? `, first of ${stats.pdbIds.length}` : ''}`}
            >
              <ExternalLinkIcon />
              <span>{stats.pdbIds.length > 1 ? `PDB ×${stats.pdbIds.length}` : 'PDB'}</span>
            </a>
          )}
          <button
            type="button"
            className={`quick-stat__action ${isSaved ? 'quick-stat__action--active' : ''}`}
            onClick={handleToggleSaved}
            disabled={Boolean(stats.localGenome)}
            title={stats.localGenome ? 'Export a local genome bundle to preserve this record' : undefined}
            aria-label={`${isSaved ? 'Saved. Remove' : 'Save'} ${stats.name}`}
            aria-pressed={isSaved}
          >
            <StarIcon filled={isSaved} />
            <span>{isSaved ? 'Saved' : 'Save'}</span>
          </button>
          <button
            type="button"
            className="quick-stat__action"
            onClick={() => stats.localGenome ? open('genomeImport') : void handleShare()}
            aria-label={stats.localGenome ? 'Export local data: genome bundle' : `${feedback === 'link-copied' ? 'Copied. Share' : 'Share'} ${selectedGeneLabel ? `${selectedGeneLabel} in ` : ''}${stats.name} explorer state`}
          >
            <ShareIcon />
            <span>{stats.localGenome ? 'Export local data' : feedback === 'link-copied' ? 'Copied' : 'Share'}</span>
          </button>
          <button
            type="button"
            className="quick-stat__action"
            onClick={() => void handleCopyCitation()}
            disabled={Boolean(stats.localGenome)}
            title={stats.localGenome ? 'This local input is not an NCBI accession citation' : undefined}
            aria-label={`${feedback === 'citation-copied' ? 'Copied. Cite' : 'Cite'} ${selectedGeneLabel ? `${selectedGeneLabel} in ` : ''}${stats.name}: copy research citation`}
          >
            <CitationIcon />
            <span>{feedback === 'citation-copied' ? 'Copied' : 'Cite'}</span>
          </button>
          <button
            type="button"
            className="quick-stat__action"
            onClick={() => void handleCopyAccession()}
            aria-label={`${feedback === 'accession-copied' ? 'Copied. Copy ID' : 'Copy ID'}: accession ${stats.accession}`}
          >
            <CopyIcon />
            <span>{feedback === 'accession-copied' ? 'Copied' : 'Copy ID'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickStats;
